/*
  Rebuy Gift Handler
  
  Manages automatic free gifts based on discount codes:
  - Works with both manually applied and URL-based discount codes
  - Adds and removes gifts based on cart value thresholds
  - Maintains single quantity for free gifts
  - Can be toggled on/off via Admin settings
*/

window.FreeGiftHandler = (function () {
  // Private variables
  let settings = null;
  let validGifts = {};

  // Constants
  const COOKIE_NAME = 'active_discount_code';
  const URL_COOKIE_NAME = 'url_discount_code';

  // Cookie utilities
  function setCookie(name, value) {
    const domain = window.location.hostname;
    const secure = window.location.protocol === 'https:';
    let cookie = `${name}=${value}; path=/; domain=${domain}; max-age=86400`;
    if (secure) cookie += '; Secure';
    document.cookie = cookie;
    // console.log(`[UK Cookie] Set: ${cookie}`);
  }

  function clearCookie(name) {
    const domain = window.location.hostname;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
    // console.log(`[UK Cookie] Cleared: ${name}`);
  }

  function getCookie(name) {
    // console.log(`[Cookie Debug] Getting cookie ${name}`);
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${name}=`))
      ?.split('=')[1];
    // console.log(`[Cookie Debug] Value for ${name}: ${cookieValue}`);
    return cookieValue;
  }

  // Helper to process gift configuration
  function setupGiftConfiguration() {
    if (!settings || !settings.freeGifts) return {};

    // Transform the freeGifts array into a map for easier lookup
    return settings.freeGifts.reduce((acc, gift) => {
      if (gift.discountCodes && gift.product) {
        gift.discountCodes.forEach(code => {
          const normalizedCode = code.trim().toUpperCase();
          if (normalizedCode) {
            acc[normalizedCode] = {
              product: String(gift.product),
              minSubtotal: Number(gift.minSubtotal),
            };
          }
        });
      }
      return acc;
    }, {});
  }

  // Detect URL-based discount codes by looking for discount messages on cart items
  function detectAutomaticDiscount() {
    const discountElements = document.querySelectorAll('.rebuy-cart__flyout-item-discount-message');
    let detectedCode = null;

    discountElements.forEach(el => {
      const discountText = el.textContent.trim().toUpperCase();
      // Check if this code matches any of our valid gift codes
      if (validGifts[discountText]) {
        detectedCode = discountText;
      }
    });

    return detectedCode;
  }

  // Handle URL-based discounts
  function handleURLDiscount() {
    const detectedDiscount = detectAutomaticDiscount();

    if (detectedDiscount) {
      // console.log(`[FreeGiftHandler] Detected automatic discount: ${detectedDiscount}`);
      setCookie(URL_COOKIE_NAME, detectedDiscount);
      return detectedDiscount;
    } else {
      clearCookie(URL_COOKIE_NAME);
      return null;
    }
  }

  // Main function to process cart and manage gifts
  async function manageGift(cart) {
    if (!cart || !cart.items || !settings.enableFreeGift) return;

    // Step 1: Get current active code from both possible sources
    const manualDiscount = getCookie(COOKIE_NAME)?.toUpperCase().trim();
    const urlDiscount = detectAutomaticDiscount();

    // Step 2: Determine which code takes precedence
    let activeCode = manualDiscount;
    if (!activeCode && urlDiscount) {
      activeCode = urlDiscount;
      setCookie(URL_COOKIE_NAME, urlDiscount);
    } else if (!activeCode && !urlDiscount) {
      clearCookie(URL_COOKIE_NAME);
    }

    // Step 3: Get the active gift configuration
    const activeGift = activeCode ? validGifts[activeCode] : null;

    // Step 4: Get cart subtotal
    const subtotal = cart.items_subtotal_price || 0;

    // Step 5: Collect all potential gift variants
    const allGiftVariants = Object.values(validGifts).map(g => g.product);

    // Step 6: Determine if a gift should be present
    let shouldHaveGift = false;
    let giftVariant = null;

    if (activeGift && subtotal >= activeGift.minSubtotal) {
      shouldHaveGift = true;
      giftVariant = activeGift.product;

      // Check if the gift is already in the cart (MATCH IT VERSION LOGIC)
      const hasGift = cart.items.some(
        item =>
          String(item.id) === String(giftVariant) &&
          item.properties &&
          item.properties._free_gift_source === 'bwb_code'
      );

      if (!hasGift) {
        await window.RebuyUtils.addGiftToCart(giftVariant);
        // Force refresh to ensure the gift was added
        if (window.Rebuy && window.Rebuy.Cart) {
          await window.Rebuy.Cart.getCart();
        }
      }
    }

    // Step 7: Remove any gifts that shouldn't be present (SIMPLIFIED LIKE IT VERSION)
    for (const item of cart.items) {
      // Check if it's one of our free gifts by looking at properties
      const isOurFreeGift = item.properties && item.properties._free_gift_source === 'bwb_code';

      // Only process free items that match our gift variants
      if (
        isOurFreeGift &&
        parseInt(item.price) === 0 &&
        allGiftVariants.includes(String(item.id)) &&
        (!shouldHaveGift || String(item.id) !== String(giftVariant))
      ) {
        await window.RebuyUtils.removeItemFromCart(item.key);
      }
    }

    // Step 8: Ensure gift quantities are always 1
    enforceGiftQuantities(cart);

    // Step 9: Clear manual discount cookie only if no valid manual code is set
    if (!manualDiscount || !validGifts[manualDiscount]) {
      clearCookie(COOKIE_NAME);
    }
  }

  // Ensure free gifts always have quantity of 1
  function enforceGiftQuantities(cart) {
    if (!cart || !cart.items) return;

    const allGiftVariants = Object.values(validGifts).map(g => g.product);

    cart.items.forEach(item => {
      // Check if it's one of our free gifts
      const isOurFreeGift = item.properties && item.properties._free_gift_source === 'bwb_code';

      if (
        isOurFreeGift &&
        parseInt(item.price) === 0 &&
        allGiftVariants.includes(String(item.id)) &&
        item.quantity > 1
      ) {
        window.RebuyUtils.setItemQuantity(item.key, 1);
      }
    });
  }

  // Handle manual discount code application
  function setupDiscountListeners() {
    document.body.addEventListener('click', async e => {
      const applyBtn = e.target.closest('.rebuy-cart__discount-form button');
      if (!applyBtn) return;

      const input = document.querySelector('#rebuy-cart__discount-input');
      const code = input?.value?.trim()?.toUpperCase();
      if (!code || !validGifts[code]) return;

      // console.log(`[UK] Applying gift code: ${code}`);
      setCookie(COOKIE_NAME, code);

      // Verify cookie was set
      await new Promise(resolve => setTimeout(resolve, 300));
      if (getCookie(COOKIE_NAME) !== code) {
        // console.warn('[UK] Cookie not persisted! Using localStorage fallback');
        localStorage.setItem(`gift_code_fallback`, code);
      }
    });
  }

  function setupRemovalListeners() {
    document.body.addEventListener('click', async e => {
      if (!e.target.closest('.rebuy-cart__discount-tag-remove')) return;

      const currentCode = getCookie(COOKIE_NAME);
      if (!currentCode) return;

      // ONLY clear if it's one of our gift codes
      if (validGifts[currentCode.toUpperCase()]) {
        // console.log(`[UK] Clearing gift code cookie: ${currentCode}`);
        clearCookie(COOKIE_NAME);
      }
    });
  }

  // Monitor for dynamically added/removed elements
  function setupDynamicListeners() {
    // Use a MutationObserver to watch for changes in the discount tag area
    const observer = new MutationObserver(() => {
      setupRemovalListeners();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Initial setup
    setupRemovalListeners();
    setupDiscountListeners();
  }

  // Public API
  return {
    init: function () {
      // console.log('[FreeGiftHandler] Initializing');

      // Get settings from window object
      settings = window.discountGiftSettings || {};

      // If free gift functionality is disabled, don't proceed
      if (!settings.enableFreeGift) {
        // console.log('[FreeGiftHandler] Free Gift functionality disabled via Admin settings');
        return this;
      }

      // Process gift configuration
      validGifts = setupGiftConfiguration();
      // console.log('[FreeGiftHandler] Valid gifts configuration:', validGifts);

      // Check if Event Manager is available
      if (!window.RebuyEventManager) {
        // console.error('[FreeGiftHandler] ERROR: RebuyEventManager is not available!');
        return this;
      }

      // Register for events
      window.RebuyEventManager.on('cart:change', data => manageGift(data.cart))
        .on('cart:ready', data => manageGift(data.cart))
        .on('cart:show', data => {
          manageGift(data.cart);
          setupDynamicListeners();
        })
        .on('discount:applied', data => manageGift(data.cart))
        .on('discount:removed', data => manageGift(data.cart));

      // console.log('[FreeGiftHandler] Successfully initialized and registered with event manager');
      return this;
    },

    // Helper method to check if a code is valid for a gift
    isValidGiftCode: function (code) {
      if (!code) return false;
      const normalizedCode = code.trim().toUpperCase();
      return !!validGifts[normalizedCode];
    },

    // Helper to get current active gift code
    getActiveCode: function () {
      // Try cookie first
      const cookieValue = getCookie(COOKIE_NAME) || getCookie(URL_COOKIE_NAME);
      if (cookieValue) return cookieValue;

      // If cookie fails, check localStorage
      try {
        return localStorage.getItem(`gift_code_${COOKIE_NAME}`);
      } catch (e) {
        return null;
      }
    },
  };
})();
